// src/components/AdminDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, BookOpen, User, LogOut, Plus, X, Edit, Search, ChevronLeft, ChevronRight, Download, Trash2, Upload, Loader2 } from 'lucide-react';
import { adminAPI } from '../services/api';
import { getAuthData, formatCurrency } from '../utils/auth';

// Loading Spinner Component
const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };

  return (
    <Loader2 className={`animate-spin ${sizeClasses[size]} ${className}`} />
  );
};

// Loading Overlay Component
const LoadingOverlay = ({ message = 'Loading...' }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4 shadow-xl">
        <LoadingSpinner size="xl" className="text-blue-600" />
        <p className="text-gray-700 font-medium">{message}</p>
      </div>
    </div>
  );
};

// Button with Loading State
const LoadingButton = ({ loading, children, disabled, className = '', loadingText, ...props }) => {
  return (
    <button
      {...props}
      disabled={loading || disabled}
      className={`${className} ${loading || disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
    >
      {loading ? (
        <span className="flex items-center gap-2">
          <LoadingSpinner size="sm" />
          {loadingText || 'Loading...'}
        </span>
      ) : children}
    </button>
  );
};

// FileUpload Component with Drag & Drop, Download, and Delete
const FileUploadField = ({ label, file, existingFileUrl, existingFileName, onFileChange, onDelete, studentId, fileType, accept = ".pdf,.jpg,.jpeg,.png" }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      onFileChange(droppedFile);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      onFileChange(selectedFile);
    }
  };

  const handleDownload = () => {
    if (existingFileUrl) {
      // Extract document ID from "db://X" format
      const documentId = existingFileUrl.replace('db://', '');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
      const downloadUrl = `${API_URL}/documents/${documentId}`;
      window.open(downloadUrl, '_blank');
    }
  };

  const handleDelete = async () => {
    if (!existingFileUrl) return;

    if (window.confirm('Are you sure you want to delete this file?')) {
      try {
        if (studentId && fileType) {
          // Call API to delete file from server
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
          const token = localStorage.getItem('token');

          const response = await fetch(`${API_URL}/admin/students/${studentId}/files/${fileType}`, {
            method: 'DELETE',
            headers: {
              'Authorization': token ? `Bearer ${token}` : '',
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error('Failed to delete file');
          }

          alert('File deleted successfully');
        }

        // Call the onDelete callback to update UI
        if (onDelete) {
          onDelete();
        }
      } catch (error) {
        console.error('Error deleting file:', error);
        alert('Failed to delete file: ' + error.message);
      }
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>

      {/* Existing file display */}
      {existingFileUrl && !file && (
        <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-blue-700 overflow-hidden">
            <Upload size={16} className="flex-shrink-0" />
            <span className="truncate" title={existingFileName || existingFileUrl}>
              {existingFileName || existingFileUrl}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleDownload}
              className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
              title="Download"
            >
              <Download size={16} />
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition"
              title="Delete"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      )}

      {/* New file selected display */}
      {file && (
        <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-green-700">
            <Upload size={16} />
            <span className="truncate max-w-xs">{file.name}</span>
          </div>
          <button
            type="button"
            onClick={() => onFileChange(null)}
            className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition"
            title="Remove"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Drag and drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragging
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 bg-white hover:border-gray-400'
        }`}
      >
        <input
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          className="hidden"
          id={`file-${label.replace(/\s+/g, '-')}`}
        />
        <label
          htmlFor={`file-${label.replace(/\s+/g, '-')}`}
          className="cursor-pointer"
        >
          <Upload className="mx-auto mb-2 text-gray-400" size={32} />
          <p className="text-sm text-gray-600 mb-1">
            <span className="text-blue-600 font-semibold">Click to upload</span> or drag and drop
          </p>
          <p className="text-xs text-gray-500">PDF, JPG, PNG (Max 5MB)</p>
        </label>
      </div>
    </div>
  );
};

// Pagination Component with Rows Per Page Selector
const PaginationControls = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemName = 'items'
}) => {
  const rowOptions = [5, 10, 15, 20, 25, 50];
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-4 pt-4 border-t">
      <div className="flex items-center gap-4">
        <div className="text-sm text-gray-700">
          Showing {startIndex} to {endIndex} of {totalItems} {itemName}
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="rowsPerPage" className="text-sm text-gray-600">Rows per page:</label>
          <select
            id="rowsPerPage"
            value={itemsPerPage}
            onChange={(e) => {
              onItemsPerPageChange(Number(e.target.value));
              onPageChange(1); // Reset to first page when changing items per page
            }}
            className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {rowOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
            className="flex items-center gap-1 px-3 py-1 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            <ChevronLeft size={16} />
            Previous
          </button>

          <div className="flex gap-1">
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  className={`px-3 py-1 rounded-lg transition ${
                    currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'border hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="flex items-center gap-1 px-3 py-1 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            Next
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

const AdminDashboard = ({ onLogout }) => {
  const { schoolId, fullName } = getAuthData();

  // Blood group options
  const bloodGroups = [
    'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'
  ];

  // Vehicle type options
  const vehicleTypes = ['2 Wheeler', '4 Wheeler'];

  // License type options
  const licenseTypes = ['New', 'Renewal'];

  // Function to calculate age from date of birth
  const calculateAge = useCallback((dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }, []);

  // Form update handlers
  const updateStudentForm = useCallback((field, value) => {
    console.log(`Updating ${field} to:`, value);
    setStudentForm(prevForm => {
      const newForm = { ...prevForm, [field]: value };
      // Auto-calculate age when date of birth changes
      if (field === 'dateOfBirth') {
        newForm.age = calculateAge(value);
      }
      console.log('New form state:', newForm);
      return newForm;
    });
  }, [calculateAge]);

  // Base state variables - must be declared first
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showAddBatch, setShowAddBatch] = useState(false);
  const [showAddTrainer, setShowAddTrainer] = useState(false);
  const [showEditStudent, setShowEditStudent] = useState(false);
  const [showEditBatch, setShowEditBatch] = useState(false);
  const [showEditTrainer, setShowEditTrainer] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editingBatch, setEditingBatch] = useState(null);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [studentPayments, setStudentPayments] = useState([]);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: 'Cash',
    transactionId: '',
    remarks: ''
  });

  // Pagination and search state
  const [currentStudentPage, setCurrentStudentPage] = useState(1);
  const [currentBatchPage, setCurrentBatchPage] = useState(1);
  const [currentTrainerPage, setCurrentTrainerPage] = useState(1);
  const [studentsPerPage, setStudentsPerPage] = useState(5);
  const [batchesPerPage, setBatchesPerPage] = useState(5);
  const [trainersPerPage, setTrainersPerPage] = useState(5);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [selectedBatchFilter, setSelectedBatchFilter] = useState(''); // For batch filtering
  const [batchSearchTerm, setBatchSearchTerm] = useState('');
  const [trainerSearchTerm, setTrainerSearchTerm] = useState('');

  // Tabs state for batch and student filtering
  const [activeBatchTab, setActiveBatchTab] = useState('active'); // 'active' or 'previous'
  const [activeStudentTab, setActiveStudentTab] = useState('active'); // 'active' or 'previous'
  const [activeBatches, setActiveBatches] = useState([]);
  const [previousBatches, setPreviousBatches] = useState([]);
  const [allBatches, setAllBatches] = useState([]); // Combined list for lookups

  // Filtered and sorted data - computed after state is declared
  const filteredAndSortedStudents = useMemo(() => {
    let filtered = students.filter(student => {
      // Text search filter
      const searchFields = [
        student.username,
        student.fullName,
        student.name,
        student.firstName,
        student.email,
        student.mobile
      ].join(' ').toLowerCase();
      const matchesSearch = searchFields.includes(studentSearchTerm.toLowerCase());

      // Batch filter
      const matchesBatch = !selectedBatchFilter ||
        (selectedBatchFilter === 'unassigned' ? !student.batchId : student.batchId === parseInt(selectedBatchFilter));

      return matchesSearch && matchesBatch;
    });

    // Sort by creation date (newest first)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt || a.createDate || a.created || 0);
      const dateB = new Date(b.createdAt || b.createDate || b.created || 0);
      return dateB - dateA;
    });
  }, [students, studentSearchTerm, selectedBatchFilter]);

  const filteredBatches = useMemo(() => {
    return batches.filter(batch => {
      const searchFields = [
        batch.batchName,
        batch.startDate,
        batch.endDate
      ].join(' ').toLowerCase();
      return searchFields.includes(batchSearchTerm.toLowerCase());
    });
  }, [batches, batchSearchTerm]);

  const filteredTrainers = useMemo(() => {
    return trainers.filter(trainer => {
      const searchFields = [
        trainer.username,
        trainer.fullName,
        trainer.name,
        trainer.email,
        trainer.mobile
      ].join(' ').toLowerCase();
      return searchFields.includes(trainerSearchTerm.toLowerCase());
    });
  }, [trainers, trainerSearchTerm]);

  // Paginated data
  const paginatedStudents = useMemo(() => {
    const startIndex = (currentStudentPage - 1) * studentsPerPage;
    return filteredAndSortedStudents.slice(startIndex, startIndex + studentsPerPage);
  }, [filteredAndSortedStudents, currentStudentPage, studentsPerPage]);

  const paginatedBatches = useMemo(() => {
    const startIndex = (currentBatchPage - 1) * batchesPerPage;
    return filteredBatches.slice(startIndex, startIndex + batchesPerPage);
  }, [filteredBatches, currentBatchPage, batchesPerPage]);

  const paginatedTrainers = useMemo(() => {
    const startIndex = (currentTrainerPage - 1) * trainersPerPage;
    return filteredTrainers.slice(startIndex, startIndex + trainersPerPage);
  }, [filteredTrainers, currentTrainerPage, trainersPerPage]);

  // Pagination info
  const studentTotalPages = Math.ceil(filteredAndSortedStudents.length / studentsPerPage);
  const batchTotalPages = Math.ceil(filteredBatches.length / batchesPerPage);
  const trainerTotalPages = Math.ceil(filteredTrainers.length / trainersPerPage);

  const initialStudentForm = {
    username: '', password: '', fullName: '', email: '', mobile: '',
    age: '', bloodGroup: '', dateOfBirth: '', vehicleType: '', licenseType: '',
    oldLicenseNumber: '',
    llrAppliedDate: '', llrObtainedDate: '', totalFee: '', paidFee: '', batchId: '',
    bloodGroupProof: null, addressProof: null, idProof: null, graduationProof: null, oldLicenseProof: null,
    bloodGroupProofUrl: '', addressProofUrl: '', idProofUrl: '', graduationProofUrl: '', oldLicenseProofUrl: '',
    bloodGroupProofFilename: '', addressProofFilename: '', idProofFilename: '', graduationProofFilename: '', oldLicenseProofFilename: ''
  };

  const [studentForm, setStudentForm] = useState(initialStudentForm);

  const [batchForm, setBatchForm] = useState({
    batchName: '', startDate: '', endDate: '', trainerId: '', startTime: ''
  });

  const [trainerForm, setTrainerForm] = useState({
    username: '', password: '', fullName: '', email: '', mobile: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      console.log('Fetching data for schoolId:', schoolId);

      // Fetch students based on active tab (fallback to all students if filter fails)
      console.log('Fetching students...');
      let studentsData;
      try {
        studentsData = await adminAPI.getStudents(
          schoolId,
          activeStudentTab === 'active' ? 'ACTIVE' : 'PREVIOUS'
        );
      } catch (err) {
        console.log('Filtered student fetch failed, trying without filter:', err.message);
        studentsData = await adminAPI.getStudents(schoolId);
      }
      console.log('Students data received:', studentsData);

      // Log individual student fields to check username availability
      if (studentsData && studentsData.length > 0) {
        console.log('First student object:', studentsData[0]);
        console.log('Available fields:', Object.keys(studentsData[0]));
        console.log('Username field:', studentsData[0].username);
      }

      // Fetch batches separately for active and previous tabs (fallback to all batches)
      console.log('Fetching batches...');
      let activeBatchesData, previousBatchesData;
      try {
        activeBatchesData = await adminAPI.getBatches(schoolId, 'ACTIVE');
        previousBatchesData = await adminAPI.getBatches(schoolId, 'PREVIOUS');
      } catch (err) {
        console.log('Filtered batch fetch failed, trying without filter:', err.message);
        const allBatches = await adminAPI.getBatches(schoolId);
        // Filter batches manually based on status field if it exists
        activeBatchesData = allBatches.filter(b => !b.status || b.status === 'ACTIVE' || b.active === true);
        previousBatchesData = allBatches.filter(b => b.status === 'CLOSED' || b.status === 'COMPLETED');
      }
      console.log('Active batches received:', activeBatchesData);
      console.log('Previous batches received:', previousBatchesData);

      console.log('Fetching trainers...');
      const trainersData = await adminAPI.getTrainers(schoolId);
      console.log('Trainers data received:', trainersData);

      setStudents(studentsData || []);
      setActiveBatches(activeBatchesData || []);
      setPreviousBatches(previousBatchesData || []);
      setBatches(activeBatchTab === 'active' ? activeBatchesData : previousBatchesData);
      setAllBatches([...(activeBatchesData || []), ...(previousBatchesData || [])]); // Combine all batches
      setTrainers(trainersData || []);

      console.log('Data successfully loaded');
    } catch (err) {
      console.error('Error fetching data:', err);
      console.error('Error details:', err.message);

      // Set empty arrays as fallback
      setStudents([]);
      setBatches([]);
      setActiveBatches([]);
      setPreviousBatches([]);
      setAllBatches([]);
      setTrainers([]);

      alert('Error loading data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = async () => {
    try {
      setSubmitting(true);
      setLoadingMessage('Creating student...');

      // Create comprehensive student data with multiple possible field name mappings
      const studentData = {
        ...studentForm,
        schoolId,
        // Convert empty date strings to null to avoid parsing errors
        dateOfBirth: studentForm.dateOfBirth || null,
        llrAppliedDate: studentForm.llrAppliedDate || null,
        llrObtainedDate: studentForm.llrObtainedDate || null,
        // Add alternative field mappings in case backend expects different names
        emailId: studentForm.email,
        emailAddress: studentForm.email,
        phoneNumber: studentForm.mobile,
        mobileNumber: studentForm.mobile,
        phone: studentForm.mobile,
        paidAmount: studentForm.paidFee,
        amountPaid: studentForm.paidFee,
        feePaid: studentForm.paidFee
      };

      console.log('Creating student with data:', studentData);
      console.log('Form state before API call:', studentForm);
      console.log('Email value:', studentForm.email);
      console.log('Mobile value:', studentForm.mobile);
      console.log('PaidFee value:', studentForm.paidFee);

      const result = await adminAPI.createStudent(studentData);
      console.log('Student creation result:', result);

      setShowAddStudent(false);
      setStudentForm(initialStudentForm);
      await fetchData();
      alert('Student created successfully!');
    } catch (err) {
      console.error('Error creating student:', err);
      alert('Error adding student: ' + err.message);
    } finally {
      setSubmitting(false);
      setLoadingMessage('');
    }
  };

  // Auto-generate batch name when time slot and dates are selected
  useEffect(() => {
    if (batchForm.startTime && batchForm.startDate && batchForm.endDate) {
      const startDate = new Date(batchForm.startDate);
      const endDate = new Date(batchForm.endDate);

      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[startDate.getMonth()];
      const year = startDate.getFullYear();

      // Format time
      const [hour, minute] = batchForm.startTime.split(':');
      const h = parseInt(hour);
      const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
      const period = h >= 12 ? 'PM' : 'AM';
      const timeStr = `${displayHour}${period}`;

      // Format dates
      const startDay = startDate.getDate();
      const endDay = endDate.getDate();

      const batchName = `${month}${year}_${timeStr}_${startDay}-${endDay}`;
      setBatchForm(prev => ({ ...prev, batchName }));
    }
  }, [batchForm.startTime, batchForm.startDate, batchForm.endDate]);

  const handleAddBatch = async () => {
    try {
      await adminAPI.createBatch({ ...batchForm, schoolId });
      setShowAddBatch(false);
      setBatchForm({ batchName: '', startDate: '', endDate: '', trainerId: '', startTime: '' });
      fetchData();
    } catch (err) {
      alert('Error adding batch: ' + err.message);
    }
  };

  const handleAddTrainer = async () => {
    try {
      await adminAPI.createTrainer({ ...trainerForm, schoolId });
      setShowAddTrainer(false);
      setTrainerForm({ username: '', password: '', fullName: '', email: '', mobile: '' });
      fetchData();
    } catch (err) {
      alert('Error adding trainer: ' + err.message);
    }
  };

  const handleEditStudent = async (student) => {
    console.log('EDIT: Student object:', student);
    console.log('EDIT: Username from student:', student.username);

    setEditingStudent(student);
    setStudentForm({
      username: student.username || '',
      password: '',
      fullName: student.fullName || student.name || student.firstName || student.studentName || '',
      email: student.email || '',
      mobile: student.mobile || '',
      age: student.age || '',
      bloodGroup: student.bloodGroup || '',
      dateOfBirth: student.dateOfBirth || '',
      vehicleType: student.vehicleType || '',
      licenseType: student.licenseType || '',
      oldLicenseNumber: student.oldLicenseNumber || '',
      llrAppliedDate: student.llrAppliedDate || '',
      llrObtainedDate: student.llrObtainedDate || '',
      totalFee: student.totalFee || '',
      paidFee: student.paidFee || '',
      batchId: student.batchId || '',
      bloodGroupProof: null,
      addressProof: null,
      idProof: null,
      graduationProof: null,
      oldLicenseProof: null,
      // Store existing file URLs
      bloodGroupProofUrl: student.bloodGroupProofUrl || '',
      addressProofUrl: student.addressProofUrl || '',
      idProofUrl: student.idProofUrl || '',
      graduationProofUrl: student.graduationProofUrl || '',
      oldLicenseProofUrl: student.oldLicenseProofUrl || '',
      // Store existing file names
      bloodGroupProofFilename: student.bloodGroupProofFilename || '',
      addressProofFilename: student.addressProofFilename || '',
      idProofFilename: student.idProofFilename || '',
      graduationProofFilename: student.graduationProofFilename || '',
      oldLicenseProofFilename: student.oldLicenseProofFilename || ''
    });

    // Fetch payment history for this student
    try {
      const payments = await adminAPI.getStudentPayments(student.id);
      setStudentPayments(payments);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setStudentPayments([]);
    }

    setShowEditStudent(true);
  };

  const handleAddPayment = async () => {
    try {
      if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
        alert('Please enter a valid payment amount');
        return;
      }

      setSubmitting(true);
      setLoadingMessage('Saving payment...');

      await adminAPI.addPayment(editingStudent.id, paymentForm);

      // Refresh payments list
      const payments = await adminAPI.getStudentPayments(editingStudent.id);
      setStudentPayments(payments);

      // Refresh student data to get updated paid/pending fees
      await fetchData();

      // Get the updated student data from the students list
      const updatedStudents = await adminAPI.getStudents(schoolId);
      const updatedStudent = updatedStudents.find(s => s.id === editingStudent.id);

      if (updatedStudent) {
        // Update the studentForm with new paid and pending fees
        setStudentForm(prev => ({
          ...prev,
          paidFee: updatedStudent.paidFee || 0,
          pendingFee: updatedStudent.pendingFee || 0
        }));

        // Update editingStudent as well
        setEditingStudent(updatedStudent);
      }

      // Reset form
      setPaymentForm({
        amount: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMode: 'Cash',
        transactionId: '',
        remarks: ''
      });
      setShowAddPayment(false);
      alert('Payment added successfully!');
    } catch (error) {
      console.error('Error adding payment:', error);
      alert('Error adding payment: ' + error.message);
    } finally {
      setSubmitting(false);
      setLoadingMessage('');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) {
      return;
    }

    try {
      await adminAPI.deletePayment(paymentId);

      // Refresh payments list
      const payments = await adminAPI.getStudentPayments(editingStudent.id);
      setStudentPayments(payments);

      // Refresh student data to get updated paid/pending fees
      await fetchData();

      // Get the updated student data from the students list
      const updatedStudents = await adminAPI.getStudents(schoolId);
      const updatedStudent = updatedStudents.find(s => s.id === editingStudent.id);

      if (updatedStudent) {
        // Update the studentForm with new paid and pending fees
        setStudentForm(prev => ({
          ...prev,
          paidFee: updatedStudent.paidFee || 0,
          pendingFee: updatedStudent.pendingFee || 0
        }));

        // Update editingStudent as well
        setEditingStudent(updatedStudent);
      }

      alert('Payment deleted successfully!');
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('Error deleting payment: ' + error.message);
    }
  };

  const handleEditBatch = (batch) => {
    setEditingBatch(batch);
    setBatchForm({
      batchName: batch.batchName || '',
      startDate: batch.startDate || '',
      endDate: batch.endDate || '',
      trainerId: batch.trainerId || ''
    });
    setShowEditBatch(true);
  };

  const handleCloseBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to close this batch? Students in this batch will be moved to previous batches.')) {
      return;
    }

    try {
      setLoading(true);
      await adminAPI.closeBatch(batchId);
      alert('Batch closed successfully');
      await fetchData(); // Refresh data
    } catch (error) {
      console.error('Error closing batch:', error);
      alert('Failed to close batch: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReopenBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to reopen this batch?')) {
      return;
    }

    try {
      setLoading(true);
      await adminAPI.reopenBatch(batchId);
      alert('Batch reopened successfully');
      await fetchData(); // Refresh data
    } catch (error) {
      console.error('Error reopening batch:', error);
      alert('Failed to reopen batch: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTrainer = (trainer) => {
    setEditingTrainer(trainer);
    setTrainerForm({
      username: trainer.username || '',
      password: '',
      fullName: trainer.fullName || '',
      email: trainer.email || '',
      mobile: trainer.mobile || ''
    });
    setShowEditTrainer(true);
  };

  const handleUpdateStudent = async () => {
    try {
      setSubmitting(true);
      setLoadingMessage('Updating student...');

      // Convert empty date strings to null to avoid parsing errors
      const updateData = {
        ...studentForm,
        schoolId,
        dateOfBirth: studentForm.dateOfBirth || null,
        llrAppliedDate: studentForm.llrAppliedDate || null,
        llrObtainedDate: studentForm.llrObtainedDate || null,
        // Convert empty string batchId to null
        batchId: studentForm.batchId && studentForm.batchId !== '' ? parseInt(studentForm.batchId) : null
      };

      await adminAPI.updateStudent(editingStudent.id, updateData);
      setShowEditStudent(false);
      setEditingStudent(null);
      setStudentForm(initialStudentForm);
      await fetchData();
      alert('Student updated successfully!');
    } catch (err) {
      alert('Error updating student: ' + err.message);
    } finally {
      setSubmitting(false);
      setLoadingMessage('');
    }
  };

  const handleUpdateBatch = async () => {
    try {
      setSubmitting(true);
      setLoadingMessage('Updating batch...');

      await adminAPI.updateBatch(editingBatch.id, { ...batchForm, schoolId });
      setShowEditBatch(false);
      setEditingBatch(null);
      setBatchForm({ batchName: '', startDate: '', endDate: '', trainerId: '' });
      await fetchData();
      alert('Batch updated successfully!');
    } catch (err) {
      alert('Error updating batch: ' + err.message);
    } finally {
      setSubmitting(false);
      setLoadingMessage('');
    }
  };

  const handleUpdateTrainer = async () => {
    try {
      setSubmitting(true);
      setLoadingMessage('Updating trainer...');

      await adminAPI.updateTrainer(editingTrainer.id, { ...trainerForm, schoolId });
      setShowEditTrainer(false);
      setEditingTrainer(null);
      setTrainerForm({ username: '', password: '', fullName: '', email: '', mobile: '' });
      await fetchData();
      alert('Trainer updated successfully!');
    } catch (err) {
      alert('Error updating trainer: ' + err.message);
    } finally {
      setSubmitting(false);
      setLoadingMessage('');
    }
  };

  const handleDeleteStudent = async (student) => {
    if (window.confirm(`Are you sure you want to delete student "${student.fullName || student.name || 'Unknown'}"?`)) {
      try {
        setDeleting(true);
        setLoadingMessage('Deleting student...');

        await adminAPI.deleteStudent(student.id);
        await fetchData();
        alert('Student deleted successfully');
      } catch (err) {
        alert('Error deleting student: ' + err.message);
      } finally {
        setDeleting(false);
        setLoadingMessage('');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Loading Overlay */}
      {(submitting || deleting || downloading) && <LoadingOverlay message={loadingMessage} />}

      {/* Header */}
      <nav className="bg-blue-600 text-white p-4 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            <p className="text-blue-100 text-sm">Welcome, {fullName}</p>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-4 py-2 bg-blue-700 rounded-lg hover:bg-blue-800 transition"
          >
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </nav>

      <div className="container mx-auto p-6">
        {/* Loading State for Initial Data */}
        {loading && !students.length && !batches.length && !trainers.length ? (
          <div className="flex flex-col items-center justify-center py-20">
            <LoadingSpinner size="xl" className="text-blue-600 mb-4" />
            <p className="text-gray-600 text-lg">Loading dashboard data...</p>
          </div>
        ) : (
          <>
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Students</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">{students.length}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Users className="text-blue-600" size={32} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Active Batches</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">{batches.length}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <BookOpen className="text-green-600" size={32} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Trainers</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">{trainers.length}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <User className="text-purple-600" size={32} />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mb-6">
          <button
            onClick={() => {
              setStudentForm(initialStudentForm);
              setShowAddStudent(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus size={20} />
            Add Student
          </button>
          <button
            onClick={() => setShowAddBatch(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            <Plus size={20} />
            Add Batch
          </button>
          <button
            onClick={() => setShowAddTrainer(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
          >
            <Plus size={20} />
            Add Trainer
          </button>
        </div>

        {/* Students Table */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">Students ({filteredAndSortedStudents.length})</h2>
            <div className="flex items-center gap-4">
              <select
                value={selectedBatchFilter}
                onChange={(e) => {
                  setSelectedBatchFilter(e.target.value);
                  setCurrentStudentPage(1); // Reset to first page when filtering
                }}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
              >
                <option value="">All Batches</option>
                <option value="unassigned">Unassigned</option>
                {(activeStudentTab === 'active' ? activeBatches : previousBatches).map(batch => (
                  <option key={batch.id} value={batch.id}>{batch.batchName}</option>
                ))}
              </select>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search students..."
                  value={studentSearchTerm}
                  onChange={(e) => {
                    setStudentSearchTerm(e.target.value);
                    setCurrentStudentPage(1); // Reset to first page when searching
                  }}
                  className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Student Tabs */}
          <div className="mb-6 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={async () => {
                  setActiveStudentTab('active');
                  setCurrentStudentPage(1);
                  setSelectedBatchFilter(''); // Reset batch filter when switching tabs
                  try {
                    const studentsData = await adminAPI.getStudents(schoolId, 'ACTIVE');
                    setStudents(studentsData);
                  } catch (error) {
                    console.error('Error fetching active students:', error);
                  }
                }}
                className={`${
                  activeStudentTab === 'active'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                Current Batch Students
              </button>
              <button
                onClick={async () => {
                  setActiveStudentTab('previous');
                  setCurrentStudentPage(1);
                  setSelectedBatchFilter(''); // Reset batch filter when switching tabs
                  try {
                    const studentsData = await adminAPI.getStudents(schoolId, 'PREVIOUS');
                    setStudents(studentsData);
                  } catch (error) {
                    console.error('Error fetching previous students:', error);
                  }
                }}
                className={`${
                  activeStudentTab === 'previous'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                Previous Batch Students
              </button>
            </nav>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Age</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Blood Group</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pending Fee</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedStudents.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      {studentSearchTerm ? 'No students found matching your search.' : 'No students found. Add your first student!'}
                    </td>
                  </tr>
                ) : (
                  paginatedStudents.map(student => (
                    <tr key={student.id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-sm font-medium">{student.username || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">{student.fullName || student.name || student.firstName || student.studentName || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">{student.age || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">{student.bloodGroup || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">
                        {allBatches.find(b => b.id === student.batchId)?.batchName || 'Not Assigned'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-red-600">
                        {formatCurrency(student.pendingFee)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditStudent(student)}
                            className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                          >
                            <Edit size={14} />
                            Edit
                          </button>
                          <button
                            onClick={async () => {
                              const hasFiles = student.bloodGroupProofUrl || student.addressProofUrl ||
                                             student.idProofUrl || student.graduationProofUrl || student.oldLicenseProofUrl;

                              if (!hasFiles) return;

                              try {
                                const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
                                const token = localStorage.getItem('token');

                                console.log('Downloading documents for student ID:', student.id);

                                const response = await fetch(`${API_URL}/admin/students/${student.id}/download-documents`, {
                                  method: 'GET',
                                  headers: {
                                    'Authorization': token ? `Bearer ${token}` : ''
                                  }
                                });

                                console.log('Download response status:', response.status);

                                if (response.ok) {
                                  const blob = await response.blob();
                                  console.log('Blob size:', blob.size);
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  const studentName = (student.name || student.fullName || 'Student').replace(/[^a-zA-Z0-9]/g, '_');
                                  const phoneNumber = student.mobile || '0000000000';
                                  a.download = `${studentName}_${phoneNumber}_documents.zip`;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                } else if (response.status === 404) {
                                  alert('No documents available to download for this student');
                                } else {
                                  const errorText = await response.text();
                                  console.error('Download error:', errorText);
                                  alert(`Failed to download documents (Status: ${response.status})`);
                                }
                              } catch (error) {
                                console.error('Error downloading documents:', error);
                                alert('Failed to download documents: ' + error.message);
                              }
                            }}
                            disabled={!(student.bloodGroupProofUrl || student.addressProofUrl ||
                                       student.idProofUrl || student.graduationProofUrl || student.oldLicenseProofUrl)}
                            className={`flex items-center gap-1 px-2 py-1 rounded transition ${
                              (student.bloodGroupProofUrl || student.addressProofUrl ||
                               student.idProofUrl || student.graduationProofUrl || student.oldLicenseProofUrl)
                                ? 'bg-orange-600 text-white hover:bg-orange-700 cursor-pointer'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                            title={(student.bloodGroupProofUrl || student.addressProofUrl ||
                                   student.idProofUrl || student.graduationProofUrl || student.oldLicenseProofUrl)
                                  ? 'Download all documents'
                                  : 'No documents available'}
                          >
                            <Download size={14} />
                            Files
                          </button>
                          <button
                            onClick={() => handleDeleteStudent(student)}
                            className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition"
                          >
                            <X size={14} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Students Pagination */}
          <PaginationControls
            currentPage={currentStudentPage}
            totalPages={studentTotalPages}
            totalItems={filteredAndSortedStudents.length}
            itemsPerPage={studentsPerPage}
            onPageChange={setCurrentStudentPage}
            onItemsPerPageChange={setStudentsPerPage}
            itemName="students"
          />
        </div>

        {/* Batches Table */}
        <div className="bg-white rounded-xl shadow-md p-6 mt-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">Batches ({filteredBatches.length})</h2>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search batches..."
                  value={batchSearchTerm}
                  onChange={(e) => {
                    setBatchSearchTerm(e.target.value);
                    setCurrentBatchPage(1);
                  }}
                  className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Batch Tabs */}
          <div className="mb-6 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => {
                  setActiveBatchTab('active');
                  setBatches(activeBatches);
                  setCurrentBatchPage(1);
                }}
                className={`${
                  activeBatchTab === 'active'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                Active Batches ({activeBatches.length})
              </button>
              <button
                onClick={() => {
                  setActiveBatchTab('previous');
                  setBatches(previousBatches);
                  setCurrentBatchPage(1);
                }}
                className={`${
                  activeBatchTab === 'previous'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                Previous Batches ({previousBatches.length})
              </button>
            </nav>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time Slot</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Students</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trainer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedBatches.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      {batchSearchTerm ? 'No batches found matching your search.' : 'No batches found. Add your first batch!'}
                    </td>
                  </tr>
                ) : (
                  paginatedBatches.map(batch => {
                    const formatTime = (timeStr) => {
                      if (!timeStr) return 'N/A';
                      const [hour, minute] = timeStr.split(':');
                      const h = parseInt(hour);
                      const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
                      const period = h >= 12 ? 'PM' : 'AM';
                      return `${displayHour}:${minute} ${period}`;
                    };

                    return (
                      <tr key={batch.id} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3 text-sm font-medium">{batch.batchName || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm">
                          {batch.startTime ? `${formatTime(batch.startTime)} - ${formatTime(batch.endTime)}` : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm">{batch.startDate || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            (batch.currentStudents || 0) >= (batch.maxStudents || 4)
                              ? 'bg-red-100 text-red-700'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {batch.currentStudents || 0}/{batch.maxStudents || 4}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {trainers.find(t => t.id === batch.trainerId)?.fullName || 'Not Assigned'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditBatch(batch)}
                              className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition"
                            >
                              <Edit size={14} />
                              Edit
                            </button>
                            {activeBatchTab === 'active' ? (
                              <button
                                onClick={() => handleCloseBatch(batch.id)}
                                className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
                                title="Close Batch"
                              >
                                Close
                              </button>
                            ) : (
                              <button
                                onClick={() => handleReopenBatch(batch.id)}
                                className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs"
                                title="Reopen Batch"
                              >
                                Reopen
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Batches Pagination */}
          <PaginationControls
            currentPage={currentBatchPage}
            totalPages={batchTotalPages}
            totalItems={filteredBatches.length}
            itemsPerPage={batchesPerPage}
            onPageChange={setCurrentBatchPage}
            onItemsPerPageChange={setBatchesPerPage}
            itemName="batches"
          />
        </div>

        {/* Trainers Table */}
        <div className="bg-white rounded-xl shadow-md p-6 mt-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">Trainers ({filteredTrainers.length})</h2>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search trainers..."
                  value={trainerSearchTerm}
                  onChange={(e) => {
                    setTrainerSearchTerm(e.target.value);
                    setCurrentTrainerPage(1);
                  }}
                  className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                />
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedTrainers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                      {trainerSearchTerm ? 'No trainers found matching your search.' : 'No trainers found. Add your first trainer!'}
                    </td>
                  </tr>
                ) : (
                  paginatedTrainers.map(trainer => (
                    <tr key={trainer.id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-sm font-medium">{trainer.fullName || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">{trainer.username || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">{trainer.email || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">{trainer.mobile || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => handleEditTrainer(trainer)}
                          className="flex items-center gap-1 px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 transition"
                        >
                          <Edit size={14} />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Trainers Pagination */}
          <PaginationControls
            currentPage={currentTrainerPage}
            totalPages={trainerTotalPages}
            totalItems={filteredTrainers.length}
            itemsPerPage={trainersPerPage}
            onPageChange={setCurrentTrainerPage}
            onItemsPerPageChange={setTrainersPerPage}
            itemName="trainers"
          />
        </div>
      </>
        )}

      {/* Add Student Modal */}
      {showAddStudent && (() => {
        console.log('CREATE MODAL: Current studentForm state:', studentForm);
        return true;
      })() && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800">Add New Student</h3>
              <button onClick={() => {
                setStudentForm(initialStudentForm);
                setShowAddStudent(false);
              }} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            {/* Profile Details */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                <h4 className="text-lg font-semibold text-gray-800">Profile Details</h4>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                    <input
                      type="text"
                      value={studentForm.username || ''}
                      onChange={(e) => updateStudentForm('username', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="Enter username"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                    <input
                      type="password"
                      value={studentForm.password || ''}
                      onChange={(e) => updateStudentForm('password', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="Enter password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      value={studentForm.fullName || ''}
                      onChange={(e) => updateStudentForm('fullName', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="Enter full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={studentForm.email || ''}
                      onChange={(e) => updateStudentForm('email', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="Enter email"
                      pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                    />
                    {studentForm.email && !studentForm.email.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/) && (
                      <p className="text-xs text-red-500 mt-1">Email must contain @ and . (e.g., user@example.com)</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile *</label>
                    <input
                      type="tel"
                      value={studentForm.mobile || ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                        if (value.length <= 10) {
                          updateStudentForm('mobile', value);
                        }
                      }}
                      pattern="[0-9]{10}"
                      maxLength="10"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="Enter 10-digit mobile number"
                    />
                    {studentForm.mobile && studentForm.mobile.length !== 10 && (
                      <p className="text-xs text-red-500 mt-1">Mobile number must be exactly 10 digits</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                    <input
                      type="date"
                      value={studentForm.dateOfBirth}
                      onChange={(e) => {
                        updateStudentForm('dateOfBirth', e.target.value);
                      }}
                      min="1940-01-01"
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                    <input
                      type="number"
                      value={studentForm.age}
                      readOnly
                      className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                      placeholder="Auto-calculated"
                    />
                    <p className="text-xs text-gray-500 mt-1">Auto-calculated from DOB</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
                    <select
                      value={studentForm.bloodGroup}
                      onChange={(e) => setStudentForm({...studentForm, bloodGroup: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Select Blood Group</option>
                      {bloodGroups.map(bloodGroup => (
                        <option key={bloodGroup} value={bloodGroup}>{bloodGroup}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type *</label>
                    <select
                      value={studentForm.vehicleType}
                      onChange={(e) => setStudentForm({...studentForm, vehicleType: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Select Vehicle Type</option>
                      {vehicleTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">License Type *</label>
                    <select
                      value={studentForm.licenseType}
                      onChange={(e) => setStudentForm({...studentForm, licenseType: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Select License Type</option>
                      {licenseTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Conditional field for Renewal license type */}
                  {studentForm.licenseType === 'Renewal' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Old License Number</label>
                      <input
                        type="text"
                        value={studentForm.oldLicenseNumber}
                        onChange={(e) => setStudentForm({...studentForm, oldLicenseNumber: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        placeholder="Enter old license number (optional)"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Batch</label>
                    <select
                      value={studentForm.batchId}
                      onChange={(e) => setStudentForm({...studentForm, batchId: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Select Batch</option>
                      {allBatches.map(batch => (
                        <option key={batch.id} value={batch.id}>{batch.batchName}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* LLR Details */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                <h4 className="text-lg font-semibold text-gray-800">LLR Details</h4>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">LLR Applied Date</label>
                    <input
                      type="date"
                      value={studentForm.llrAppliedDate}
                      onChange={(e) => setStudentForm({...studentForm, llrAppliedDate: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">LLR Obtained Date</label>
                    <input
                      type="date"
                      value={studentForm.llrObtainedDate}
                      onChange={(e) => setStudentForm({...studentForm, llrObtainedDate: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Details */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                <h4 className="text-lg font-semibold text-gray-800">Payment Details</h4>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Fee *</label>
                    <input
                      type="number"
                      value={studentForm.totalFee}
                      onChange={(e) => setStudentForm({...studentForm, totalFee: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="Enter total fee"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid</label>
                    <input
                      type="number"
                      value={studentForm.paidFee || ''}
                      onChange={(e) => updateStudentForm('paidFee', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="Enter amount paid"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Balance Due</label>
                    <input
                      type="number"
                      value={(parseFloat(studentForm.totalFee || 0) - parseFloat(studentForm.paidFee || 0)) || 0}
                      readOnly
                      className="w-full px-3 py-2 border rounded-lg bg-red-50 text-red-700 font-semibold cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Auto-calculated</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ID Proof Section */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                <h4 className="text-lg font-semibold text-gray-800">ID Proof & Documents</h4>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FileUploadField
                    label="Blood Group Proof"
                    file={studentForm.bloodGroupProof}
                    onFileChange={(file) => setStudentForm({...studentForm, bloodGroupProof: file})}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />

                  <FileUploadField
                    label="Address Proof"
                    file={studentForm.addressProof}
                    onFileChange={(file) => setStudentForm({...studentForm, addressProof: file})}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />

                  <FileUploadField
                    label="ID Proof (Aadhar/PAN/Driving License)"
                    file={studentForm.idProof}
                    onFileChange={(file) => setStudentForm({...studentForm, idProof: file})}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />

                  <FileUploadField
                    label="Graduation Proof"
                    file={studentForm.graduationProof}
                    onFileChange={(file) => setStudentForm({...studentForm, graduationProof: file})}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />

                  {/* Conditional Old License Proof for Renewal - Add Student */}
                  {studentForm.licenseType === 'Renewal' && (
                    <FileUploadField
                      label="Old License Proof"
                      file={studentForm.oldLicenseProof}
                      onFileChange={(file) => setStudentForm({...studentForm, oldLicenseProof: file})}
                      accept=".pdf,.jpg,.jpeg,.png"
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 border-t pt-4">
              <button
                onClick={() => {
                  setStudentForm(initialStudentForm);
                  setShowAddStudent(false);
                }}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <LoadingButton
                loading={submitting}
                loadingText="Creating..."
                onClick={handleAddStudent}
                className="flex-1 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                Add Student
              </LoadingButton>
            </div>
          </div>
        </div>
      )}

      {/* Add Batch Modal */}
      {showAddBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Add New Batch</h3>
              <button onClick={() => setShowAddBatch(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                <input
                  type="date"
                  value={batchForm.startDate}
                  onChange={(e) => setBatchForm({...batchForm, startDate: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                <input
                  type="date"
                  value={batchForm.endDate}
                  onChange={(e) => setBatchForm({...batchForm, endDate: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time Slot (60 minutes) *</label>
                <select
                  value={batchForm.startTime || ''}
                  onChange={(e) => setBatchForm({...batchForm, startTime: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                >
                  <option value="">Select Time Slot</option>
                  {Array.from({length: 12}, (_, i) => {
                    const hour = 6 + i;
                    const time = `${hour.toString().padStart(2, '0')}:00`;
                    const displayTime = hour < 12 ? `${hour}:00 AM` : hour === 12 ? `12:00 PM` : `${hour-12}:00 PM`;
                    const endHour = hour + 1;
                    const endDisplayTime = endHour < 12 ? `${endHour}:00 AM` : endHour === 12 ? `12:00 PM` : `${endHour-12}:00 PM`;
                    return (
                      <option key={time} value={time}>
                        {displayTime} - {endDisplayTime}
                      </option>
                    );
                  })}
                </select>
              </div>
              {batchForm.batchName && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Batch Name (Auto-generated)</label>
                  <input
                    type="text"
                    value={batchForm.batchName}
                    readOnly
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-700 font-medium cursor-not-allowed"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trainer</label>
                <select
                  value={batchForm.trainerId}
                  onChange={(e) => setBatchForm({...batchForm, trainerId: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                >
                  <option value="">Select Trainer</option>
                  {trainers.map(trainer => (
                    <option key={trainer.id} value={trainer.id}>{trainer.fullName}</option>
                  ))}
                </select>
              </div>
              <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                <p>• Batch name auto-generates from date and time</p>
                <p>• Each batch is 60 minutes</p>
                <p>• Maximum 4 students per batch</p>
                <p>• Time slots: 6:00 AM - 6:00 PM</p>
              </div>
            </div>
            <button
              onClick={handleAddBatch}
              className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              Add Batch
            </button>
          </div>
        </div>
      )}

      {/* Add Trainer Modal */}
      {showAddTrainer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Add New Trainer</h3>
              <button onClick={() => setShowAddTrainer(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Username"
                value={trainerForm.username}
                onChange={(e) => setTrainerForm({...trainerForm, username: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
              <input
                type="password"
                placeholder="Password"
                value={trainerForm.password}
                onChange={(e) => setTrainerForm({...trainerForm, password: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
              <input
                type="text"
                placeholder="Full Name"
                value={trainerForm.fullName}
                onChange={(e) => setTrainerForm({...trainerForm, fullName: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
              <div>
                <input
                  type="email"
                  placeholder="Email"
                  value={trainerForm.email}
                  onChange={(e) => setTrainerForm({...trainerForm, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                />
                {trainerForm.email && !trainerForm.email.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/) && (
                  <p className="text-xs text-red-500 mt-1">Email must contain @ and . (e.g., user@example.com)</p>
                )}
              </div>
              <input
                type="tel"
                placeholder="Mobile"
                value={trainerForm.mobile}
                onChange={(e) => setTrainerForm({...trainerForm, mobile: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
            <button
              onClick={handleAddTrainer}
              className="mt-4 w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              Add Trainer
            </button>
          </div>
        </div>
      )}

      {/* Edit Student Modal */}
      {showEditStudent && editingStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800">Edit Student</h3>
              <button onClick={() => setShowEditStudent(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            {/* Profile Details */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                <h4 className="text-lg font-semibold text-gray-800">Profile Details</h4>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                    <input
                      type="text"
                      value={studentForm.username}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed outline-none select-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">Cannot be changed</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      value={studentForm.fullName}
                      onChange={(e) => setStudentForm({...studentForm, fullName: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={studentForm.email}
                      onChange={(e) => setStudentForm({...studentForm, email: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                    />
                    {studentForm.email && !studentForm.email.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/) && (
                      <p className="text-xs text-red-500 mt-1">Email must contain @ and . (e.g., user@example.com)</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile *</label>
                    <input
                      type="tel"
                      value={studentForm.mobile}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                        if (value.length <= 10) {
                          setStudentForm({...studentForm, mobile: value});
                        }
                      }}
                      pattern="[0-9]{10}"
                      maxLength="10"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="Enter 10-digit mobile number"
                    />
                    {studentForm.mobile && studentForm.mobile.length !== 10 && (
                      <p className="text-xs text-red-500 mt-1">Mobile number must be exactly 10 digits</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                    <input
                      type="date"
                      value={studentForm.dateOfBirth}
                      onChange={(e) => {
                        const newAge = calculateAge(e.target.value);
                        setStudentForm({...studentForm, dateOfBirth: e.target.value, age: newAge});
                      }}
                      min="1940-01-01"
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                    <input
                      type="number"
                      value={studentForm.age}
                      readOnly
                      className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Auto-calculated from DOB</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
                    <select
                      value={studentForm.bloodGroup}
                      onChange={(e) => setStudentForm({...studentForm, bloodGroup: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Select Blood Group</option>
                      {bloodGroups.map(bloodGroup => (
                        <option key={bloodGroup} value={bloodGroup}>{bloodGroup}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type *</label>
                    <select
                      value={studentForm.vehicleType}
                      onChange={(e) => setStudentForm({...studentForm, vehicleType: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Select Vehicle Type</option>
                      {vehicleTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">License Type *</label>
                    <select
                      value={studentForm.licenseType}
                      onChange={(e) => setStudentForm({...studentForm, licenseType: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Select License Type</option>
                      {licenseTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Conditional field for Renewal license type */}
                  {studentForm.licenseType === 'Renewal' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Old License Number</label>
                      <input
                        type="text"
                        value={studentForm.oldLicenseNumber}
                        onChange={(e) => setStudentForm({...studentForm, oldLicenseNumber: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        placeholder="Enter old license number (optional)"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Batch</label>
                    <select
                      value={studentForm.batchId}
                      onChange={(e) => setStudentForm({...studentForm, batchId: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Select Batch</option>
                      {allBatches.map(batch => (
                        <option key={batch.id} value={batch.id}>{batch.batchName}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* LLR Details */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                <h4 className="text-lg font-semibold text-gray-800">LLR Details</h4>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">LLR Applied Date</label>
                    <input
                      type="date"
                      value={studentForm.llrAppliedDate}
                      onChange={(e) => setStudentForm({...studentForm, llrAppliedDate: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">LLR Obtained Date</label>
                    <input
                      type="date"
                      value={studentForm.llrObtainedDate}
                      onChange={(e) => setStudentForm({...studentForm, llrObtainedDate: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Details */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                <h4 className="text-lg font-semibold text-gray-800">Payment Details</h4>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Fee *</label>
                    <input
                      type="number"
                      value={studentForm.totalFee}
                      onChange={(e) => setStudentForm({...studentForm, totalFee: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid</label>
                    <input
                      type="number"
                      value={studentForm.paidFee}
                      readOnly
                      className="w-full px-3 py-2 border rounded-lg bg-green-50 text-green-700 font-semibold cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Auto-calculated from payments</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Balance Due</label>
                    <input
                      type="number"
                      value={(parseFloat(studentForm.totalFee || 0) - parseFloat(studentForm.paidFee || 0)) || 0}
                      readOnly
                      className="w-full px-3 py-2 border rounded-lg bg-red-50 text-red-700 font-semibold cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Auto-calculated</p>
                  </div>
                </div>

                {/* Payment History */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex justify-between items-center mb-4">
                    <h5 className="font-semibold text-gray-800">Payment History</h5>
                    <div className="flex gap-2">
                      {studentPayments.length > 0 && (
                        <button
                          onClick={async () => {
                            try {
                              const blob = await adminAPI.downloadPaymentHistory(editingStudent.id);
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              const studentName = editingStudent.name || 'Student';
                              const phoneNumber = editingStudent.mobile || '0000000000';
                              a.download = `${studentName}_${phoneNumber}_payment_history.csv`;
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                            } catch (error) {
                              console.error('Error downloading payment history:', error);
                              alert('Failed to download payment history');
                            }
                          }}
                          className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                        >
                          <Download size={16} />
                          Download CSV
                        </button>
                      )}
                      <button
                        onClick={() => setShowAddPayment(!showAddPayment)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
                      >
                        <Plus size={16} />
                        Add Payment
                      </button>
                    </div>
                  </div>

                  {/* Add Payment Form */}
                  {showAddPayment && (
                    <div className="bg-white p-4 rounded-lg border-2 border-green-200 mb-4">
                      <h6 className="font-semibold text-gray-800 mb-3">New Payment</h6>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                          <input
                            type="number"
                            value={paymentForm.amount}
                            onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                            placeholder="Enter amount"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
                          <input
                            type="date"
                            value={paymentForm.paymentDate}
                            onChange={(e) => setPaymentForm({...paymentForm, paymentDate: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
                          <select
                            value={paymentForm.paymentMode}
                            onChange={(e) => setPaymentForm({...paymentForm, paymentMode: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                          >
                            <option value="Cash">Cash</option>
                            <option value="Card">Card</option>
                            <option value="UPI">UPI</option>
                            <option value="Bank Transfer">Bank Transfer</option>
                            <option value="Cheque">Cheque</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID</label>
                          <input
                            type="text"
                            value={paymentForm.transactionId}
                            onChange={(e) => setPaymentForm({...paymentForm, transactionId: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                            placeholder="Optional"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                          <input
                            type="text"
                            value={paymentForm.remarks}
                            onChange={(e) => setPaymentForm({...paymentForm, remarks: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                            placeholder="Optional notes"
                          />
                        </div>
                      </div>

                      <div className="flex gap-2 mt-4">
                        <LoadingButton
                          loading={submitting}
                          loadingText="Saving..."
                          onClick={handleAddPayment}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                        >
                          Save Payment
                        </LoadingButton>
                        <button
                          onClick={() => {
                            setShowAddPayment(false);
                            setPaymentForm({
                              amount: '',
                              paymentDate: new Date().toISOString().split('T')[0],
                              paymentMode: 'Cash',
                              transactionId: '',
                              remarks: ''
                            });
                          }}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Payment History Table */}
                  {studentPayments.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border rounded-lg">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Date</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Amount</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Mode</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Transaction ID</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Remarks</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {studentPayments.map((payment) => (
                            <tr key={payment.id} className="border-t hover:bg-gray-50">
                              <td className="px-4 py-2 text-sm text-gray-700">{payment.paymentDate}</td>
                              <td className="px-4 py-2 text-sm font-semibold text-green-600">₹{payment.amount}</td>
                              <td className="px-4 py-2 text-sm text-gray-700">{payment.paymentMode}</td>
                              <td className="px-4 py-2 text-sm text-gray-700">{payment.transactionId || '-'}</td>
                              <td className="px-4 py-2 text-sm text-gray-700">{payment.remarks || '-'}</td>
                              <td className="px-4 py-2">
                                <button
                                  onClick={() => handleDeletePayment(payment.id)}
                                  className="text-red-600 hover:text-red-800 transition"
                                  title="Delete Payment"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No payment history available</p>
                      <p className="text-sm">Click "Add Payment" to record a payment</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ID Proof Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                  <h4 className="text-lg font-semibold text-gray-800">ID Proof & Documents</h4>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
                      const token = localStorage.getItem('token');

                      console.log('Downloading documents for student ID:', editingStudent.id);
                      console.log('API URL:', API_URL);

                      const response = await fetch(`${API_URL}/admin/students/${editingStudent.id}/download-documents`, {
                        method: 'GET',
                        headers: {
                          'Authorization': token ? `Bearer ${token}` : ''
                        }
                      });

                      console.log('Download response status:', response.status);

                      if (response.ok) {
                        const blob = await response.blob();
                        console.log('Blob size:', blob.size);
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        const studentName = editingStudent.name || 'Student';
                        const phoneNumber = editingStudent.mobile || '0000000000';
                        a.download = `${studentName}_${phoneNumber}_documents.zip`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      } else if (response.status === 404) {
                        alert('No documents available to download for this student');
                      } else {
                        const errorText = await response.text();
                        console.error('Download error:', errorText);
                        alert(`Failed to download documents (Status: ${response.status})`);
                      }
                    } catch (error) {
                      console.error('Error downloading documents:', error);
                      alert('Failed to download documents: ' + error.message);
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm"
                >
                  <Download size={16} />
                  Download All Documents
                </button>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FileUploadField
                    label="Blood Group Proof"
                    file={studentForm.bloodGroupProof}
                    existingFileUrl={studentForm.bloodGroupProofUrl}
                    existingFileName={studentForm.bloodGroupProofFilename}
                    studentId={editingStudent?.id}
                    fileType="bloodGroupProof"
                    onFileChange={(file) => setStudentForm({...studentForm, bloodGroupProof: file})}
                    onDelete={() => {
                      setStudentForm({...studentForm, bloodGroupProofUrl: '', bloodGroupProofFilename: '', bloodGroupProof: null});
                      fetchData(); // Refresh data after delete
                    }}
                  />

                  <FileUploadField
                    label="Address Proof"
                    file={studentForm.addressProof}
                    existingFileUrl={studentForm.addressProofUrl}
                    existingFileName={studentForm.addressProofFilename}
                    studentId={editingStudent?.id}
                    fileType="addressProof"
                    onFileChange={(file) => setStudentForm({...studentForm, addressProof: file})}
                    onDelete={() => {
                      setStudentForm({...studentForm, addressProofUrl: '', addressProofFilename: '', addressProof: null});
                      fetchData(); // Refresh data after delete
                    }}
                  />

                  <FileUploadField
                    label="ID Proof (Aadhar/PAN/Driving License)"
                    file={studentForm.idProof}
                    existingFileUrl={studentForm.idProofUrl}
                    existingFileName={studentForm.idProofFilename}
                    studentId={editingStudent?.id}
                    fileType="idProof"
                    onFileChange={(file) => setStudentForm({...studentForm, idProof: file})}
                    onDelete={() => {
                      setStudentForm({...studentForm, idProofUrl: '', idProofFilename: '', idProof: null});
                      fetchData(); // Refresh data after delete
                    }}
                  />

                  <FileUploadField
                    label="Graduation Proof"
                    file={studentForm.graduationProof}
                    existingFileUrl={studentForm.graduationProofUrl}
                    existingFileName={studentForm.graduationProofFilename}
                    studentId={editingStudent?.id}
                    fileType="graduationProof"
                    onFileChange={(file) => setStudentForm({...studentForm, graduationProof: file})}
                    onDelete={() => {
                      setStudentForm({...studentForm, graduationProofUrl: '', graduationProofFilename: '', graduationProof: null});
                      fetchData(); // Refresh data after delete
                    }}
                  />

                  {/* Conditional Old License Proof for Renewal - Edit Student */}
                  {studentForm.licenseType === 'Renewal' && (
                    <FileUploadField
                      label="Old License Proof"
                      file={studentForm.oldLicenseProof}
                      existingFileUrl={studentForm.oldLicenseProofUrl}
                      existingFileName={studentForm.oldLicenseProofFilename}
                      studentId={editingStudent?.id}
                      fileType="oldLicenseProof"
                      onFileChange={(file) => setStudentForm({...studentForm, oldLicenseProof: file})}
                      onDelete={() => {
                        setStudentForm({...studentForm, oldLicenseProofUrl: '', oldLicenseProofFilename: '', oldLicenseProof: null});
                        fetchData(); // Refresh data after delete
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 border-t pt-4">
              <button
                onClick={() => setShowEditStudent(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteStudent(editingStudent)}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                Delete Student
              </button>
              <LoadingButton
                loading={submitting}
                loadingText="Updating..."
                onClick={handleUpdateStudent}
                className="flex-1 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                Update Student
              </LoadingButton>
            </div>
          </div>
        </div>
      )}

      {/* Edit Batch Modal */}
      {showEditBatch && editingBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Edit Batch</h3>
              <button onClick={() => setShowEditBatch(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Batch Name"
                value={batchForm.batchName}
                onChange={(e) => setBatchForm({...batchForm, batchName: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
              />
              <input
                type="date"
                placeholder="Start Date"
                value={batchForm.startDate}
                onChange={(e) => setBatchForm({...batchForm, startDate: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
              />
              <input
                type="date"
                placeholder="End Date"
                value={batchForm.endDate}
                onChange={(e) => setBatchForm({...batchForm, endDate: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
              />
              <select
                value={batchForm.trainerId}
                onChange={(e) => setBatchForm({...batchForm, trainerId: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
              >
                <option value="">Select Trainer</option>
                {trainers.map(trainer => (
                  <option key={trainer.id} value={trainer.id}>{trainer.fullName}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleUpdateBatch}
              className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              Update Batch
            </button>
          </div>
        </div>
      )}

      {/* Edit Trainer Modal */}
      {showEditTrainer && editingTrainer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Edit Trainer</h3>
              <button onClick={() => setShowEditTrainer(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Username"
                value={trainerForm.username}
                onChange={(e) => setTrainerForm({...trainerForm, username: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
              <input
                type="password"
                placeholder="Password (leave blank to keep current)"
                value={trainerForm.password}
                onChange={(e) => setTrainerForm({...trainerForm, password: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
              <input
                type="text"
                placeholder="Full Name"
                value={trainerForm.fullName}
                onChange={(e) => setTrainerForm({...trainerForm, fullName: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
              <div>
                <input
                  type="email"
                  placeholder="Email"
                  value={trainerForm.email}
                  onChange={(e) => setTrainerForm({...trainerForm, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                />
                {trainerForm.email && !trainerForm.email.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/) && (
                  <p className="text-xs text-red-500 mt-1">Email must contain @ and . (e.g., user@example.com)</p>
                )}
              </div>
              <input
                type="tel"
                placeholder="Mobile"
                value={trainerForm.mobile}
                onChange={(e) => setTrainerForm({...trainerForm, mobile: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
            <button
              onClick={handleUpdateTrainer}
              className="mt-4 w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              Update Trainer
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AdminDashboard;